// F-075 : décompte et restitution du dépôt de garantie (bailleur) et vue du locataire.
import { useState } from "react";
import { del, patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, money } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, ErrorBox, FileDrop, Input, KeyValues, Loading, MoneyInput, Notice, Select, Table, Textarea, fieldError } from "@core/ui";
import { DEDUCTION_KINDS, DEPOSIT_STATUS, RuleNote, Status, docUrl, options, orgApi, sendFile, todayIso, useAction } from "./shared";

/** Tableau du décompte : dépôt, loyers, charges, retenues et solde, distingués. */
function Breakdown({ d }: { d: any }) {
  const c = d.computation;
  const rows: [string, string, number][] = [["Dépôt de garantie détenu", "Dépôt", c.deposit_cents]];
  if (c.unpaid_rent_cents) rows.push(["Loyers restant dus", "Loyers", -c.unpaid_rent_cents]);
  if (c.unpaid_charges_cents) rows.push(["Charges restant dues", "Charges", -c.unpaid_charges_cents]);
  if (c.charges_regularization_due_cents) rows.push(["Régularisation des charges (reste dû)", "Charges", -c.charges_regularization_due_cents]);
  if (c.charges_regularization_credit_cents) rows.push(["Régularisation des charges (trop-perçu)", "Charges", c.charges_regularization_credit_cents]);
  for (const x of d.deductions) rows.push([`${x.label} — ${x.justification}${x.inspection_reference ? ` (état des lieux : ${x.inspection_reference})` : ""}${x.estimate ? " · devis estimatif" : ""}`, "Retenue", -x.amount_cents]);
  if (c.holdback_cents) rows.push(["Retenue provisoire (arrêté des comptes de copropriété)", "Dépôt", -c.holdback_cents]);
  return (
    <Table caption="Décompte du dépôt de garantie">
      <thead>
        <tr>
          <th>Libellé</th>
          <th>Nature</th>
          <th className="right">Montant</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([l, n, a], i) => (
          <tr key={i}>
            <td>{l}</td>
            <td>{n}</td>
            <td className="vie-amount">{money(a)}</td>
          </tr>
        ))}
        <tr className="vie-total">
          <td>{c.remaining_due_cents ? "Reste dû par le locataire" : "Montant à restituer"}</td>
          <td>Solde</td>
          <td className="vie-amount">{money(c.remaining_due_cents || c.refund_now_cents)}</td>
        </tr>
      </tbody>
    </Table>
  );
}

function Deadline({ d }: { d: any }) {
  const s = d.statement;
  return (
    <KeyValues
      items={[
        ["Remise des clés", date(s.keys_returned_on)],
        ["État des lieux de sortie", s.exit_inspection_conform === null ? "À préciser" : s.exit_inspection_conform ? "Conforme à l’entrée (délai d’un mois)" : "Différences relevées (délai de deux mois)"],
        ["Restitution au plus tard le", date(s.due_on)],
        ["Déjà restitué", money(d.refunded_cents)],
        ["Majoration pour retard", d.penalty && d.penalty.months_started ? `${money(d.penalty.total_cents)} (${d.penalty.months_started} mois commencé${d.penalty.months_started > 1 ? "s" : ""} × ${money(d.penalty.per_month_cents)})` : "Aucune à ce jour"],
      ]}
    />
  );
}

/** Panneau du bailleur, dans l'onglet « Départ et dépôt » du bail. */
export function DepositPanel({ orgId, leaseId }: { orgId: string; leaseId: string }) {
  const org = useOrg();
  const path = orgApi(orgId, `/leases/${leaseId}/deposit-statement`);
  const q = useGet<any>(path);
  const invalidate = useInvalidate();
  const refresh = () => invalidate(path, orgApi(orgId, "/deposit-statements"), orgApi(orgId, "/agenda"));
  const act = useAction();
  const d = q.data?.deposit;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!d) {
    return (
      <Card title="Dépôt de garantie">
        <p>
          Dépôt détenu d’après le bail : <strong>{money(q.data.lease.deposit_cents)}</strong>.
        </p>
        <RuleNote>{q.data.rule_text}</RuleNote>
        <ErrorBox error={act.error} />
        {org.can("operate") ? (
          <Button busy={act.busy} onClick={async () => { await act.run(() => post(path, {}), "Décompte préparé à partir des appels de loyer. Vérifiez les montants."); refresh(); }}>
            Préparer le décompte
          </Button>
        ) : null}
      </Card>
    );
  }
  const s = d.statement;
  const base = orgApi(orgId, `/deposit-statements/${s.id}`);
  const draft = s.status === "brouillon";
  return (
    <Card title="Dépôt de garantie" actions={<Status value={s.status} labels={DEPOSIT_STATUS} />}>
      <div className="stack">
        <ErrorBox error={act.error} />
        {d.warnings.map((w: string) => (
          <Notice key={w} tone="attention">
            {w}
          </Notice>
        ))}
        <Deadline d={d} />
        {draft && org.can("operate") ? <DraftFields base={base} s={s} max={d.max_holdback_cents} onDone={refresh} /> : null}
        <Breakdown d={d} />
        {draft && org.can("operate") ? (
          <>
            {d.deductions.length ? (
              <ul className="list-plain">
                {d.deductions.map((x: any) => (
                  <li key={x.id} className="row-between">
                    <span className="small">
                      {x.label} · {money(x.amount_cents)}
                      {x.document_id ? (
                        <>
                          {" "}
                          · <a href={docUrl(x.document_id)} target="_blank" rel="noreferrer">justificatif</a>
                        </>
                      ) : null}
                    </span>
                    <Button small variant="discret" onClick={async () => { await act.run(() => del(`${base}/deductions/${x.id}`), "Retenue retirée."); refresh(); }}>
                      Retirer
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
            <DeductionForm base={base} onDone={refresh} />
          </>
        ) : null}
        <p className="small muted">{d.rappel}</p>
        <RuleNote version={s.rule_version}>{d.rule_text}</RuleNote>
        {d.refund_split.length > 1 ? (
          <p className="small">
            Répartition proposée entre colocataires : {d.refund_split.map((p: any) => `${p.full_name} ${money(p.amount_cents)}`).join(" · ")}
          </p>
        ) : null}
        <div className="row">
          {draft && org.can("finance") ? (
            <Button busy={act.busy} onClick={async () => { await act.run(() => post(`${base}/validate`, {}), "Décompte validé et transmis au locataire."); refresh(); }}>
              Valider le décompte
            </Button>
          ) : null}
          {draft && !org.can("finance") ? <p className="small muted">La validation est réservée aux membres qui gèrent les finances.</p> : null}
          {s.document_id ? (
            <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={docUrl(s.document_id)} target="_blank" rel="noreferrer">
              Décompte PDF
            </a>
          ) : null}
          {s.status === "valide" && org.can("finance") && !d.refunds.length ? (
            <Button small variant="discret" onClick={async () => { await act.run(() => post(`${base}/reopen`, {}), "Décompte rouvert."); refresh(); }}>
              Rouvrir
            </Button>
          ) : null}
          {["valide", "restitue_partiellement", "restitue"].includes(s.status) && org.can("finance") ? (
            <Button small variant="discret" onClick={async () => { await act.run(() => post(`${base}/close`, {}), "Décompte clos."); refresh(); }}>
              Clore
            </Button>
          ) : null}
        </div>
        {["valide", "restitue_partiellement"].includes(s.status) && org.can("finance") ? <RefundForm base={base} expected={d.remaining_to_refund_cents} onDone={refresh} /> : null}
        {d.refunds.length ? (
          <Table caption="Restitutions">
            <thead>
              <tr>
                <th>Date</th>
                <th>Mode</th>
                <th>Référence</th>
                <th className="right">Montant</th>
              </tr>
            </thead>
            <tbody>
              {d.refunds.map((r: any) => (
                <tr key={r.id}>
                  <td className="num">{date(r.paid_on)}</td>
                  <td>{r.method}</td>
                  <td>{r.reference}</td>
                  <td className="vie-amount">{money(r.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </div>
    </Card>
  );
}

function DraftFields({ base, s, max, onDone }: { base: string; s: any; max: number; onDone: () => void }) {
  const [f, setF] = useState({
    keys_returned_on: s.keys_returned_on ?? "",
    conform: s.exit_inspection_conform === null ? "" : s.exit_inspection_conform ? "oui" : "non",
    unpaid_rent_cents: s.unpaid_rent_cents as number | null,
    unpaid_charges_cents: s.unpaid_charges_cents as number | null,
    regul_sens: s.charges_regularization_cents < 0 ? "credit" : "du",
    regul_cents: Math.abs(s.charges_regularization_cents) as number | null,
    holdback_cents: s.provisional_holdback_cents as number | null,
    notes: s.notes,
  });
  const act = useAction();
  return (
    <form
      className="stack-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        const regul = (f.regul_cents ?? 0) * (f.regul_sens === "credit" ? -1 : 1);
        await act.run(
          () =>
            patch(base, {
              keys_returned_on: f.keys_returned_on || undefined,
              exit_inspection_conform: f.conform === "" ? undefined : f.conform === "oui",
              unpaid_rent_cents: f.unpaid_rent_cents ?? 0,
              unpaid_charges_cents: f.unpaid_charges_cents ?? 0,
              charges_regularization_cents: regul,
              provisional_holdback_cents: f.holdback_cents ?? 0,
              notes: f.notes,
            }),
          "Décompte mis à jour.",
        );
        onDone();
      }}
    >
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Input label="Remise des clés le" type="date" value={f.keys_returned_on} error={fieldError(act.error, "keys_returned_on")} onChange={(e) => setF({ ...f, keys_returned_on: e.target.value })} />
        <Select
          label="État des lieux de sortie"
          value={f.conform}
          onChange={(e) => setF({ ...f, conform: e.target.value })}
          options={[
            { value: "", label: "À préciser" },
            { value: "oui", label: "Conforme à l’entrée" },
            { value: "non", label: "Différences relevées" },
          ]}
          help="Comparaison faite avec le module d’entrée ; vous décidez."
        />
      </div>
      <div className="form-row">
        <MoneyInput label="Loyers restant dus" cents={f.unpaid_rent_cents} onChange={(c) => setF({ ...f, unpaid_rent_cents: c })} help="Proposé depuis les appels de loyer non réglés, à vérifier." />
        <MoneyInput label="Charges restant dues" cents={f.unpaid_charges_cents} onChange={(c) => setF({ ...f, unpaid_charges_cents: c })} />
      </div>
      <div className="form-row">
        <Select label="Régularisation de charges" value={f.regul_sens} onChange={(e) => setF({ ...f, regul_sens: e.target.value })} options={[{ value: "du", label: "Reste dû par le locataire" }, { value: "credit", label: "Trop-perçu à rendre" }]} />
        <MoneyInput label="Montant de la régularisation" cents={f.regul_cents} onChange={(c) => setF({ ...f, regul_cents: c })} />
        <MoneyInput label="Retenue provisoire (copropriété)" cents={f.holdback_cents} error={fieldError(act.error, "provisional_holdback_cents")} help={`20 % du dépôt au plus, soit ${money(max)}.`} onChange={(c) => setF({ ...f, holdback_cents: c })} />
      </div>
      <Textarea label="Notes internes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <div>
        <Button type="submit" variant="secondaire" busy={act.busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

function DeductionForm({ base, onDone }: { base: string; onDone: () => void }) {
  const [f, setF] = useState({ kind: "reparation", label: "", amount_cents: null as number | null, justification: "", inspection_reference: "", estimate: false });
  const [doc, setDoc] = useState<{ id: string; filename: string } | null>(null);
  const act = useAction();
  return (
    <form
      className="stack-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await act.run(() => post(`${base}/deductions`, { ...f, amount_cents: f.amount_cents ?? 0, document_id: doc?.id }), "Retenue ajoutée.")) {
          setF({ kind: "reparation", label: "", amount_cents: null, justification: "", inspection_reference: "", estimate: false });
          setDoc(null);
          onDone();
        }
      }}
    >
      <h3>Ajouter une retenue justifiée</h3>
      <ErrorBox error={act.error} />
      <div className="form-row">
        <Select label="Nature" value={f.kind} options={options(DEDUCTION_KINDS)} onChange={(e) => setF({ ...f, kind: e.target.value })} />
        <Input label="Libellé" required value={f.label} error={fieldError(act.error, "label")} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <MoneyInput label="Montant" required cents={f.amount_cents} error={fieldError(act.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
      </div>
      <Input label="Justification" required value={f.justification} error={fieldError(act.error, "justification")} onChange={(e) => setF({ ...f, justification: e.target.value })} />
      <Textarea
        label="Écart relevé à l’état des lieux (référence ou texte collé)"
        rows={2}
        value={f.inspection_reference}
        error={fieldError(act.error, "inspection_reference")}
        onChange={(e) => setF({ ...f, inspection_reference: e.target.value })}
        help="Pièce, élément et constat. Un écart ne vaut pas à lui seul une dégradation imputable : tenez compte de la vétusté."
      />
      <FileDrop
        label={doc ? `Justificatif : ${doc.filename}` : "Joindre une facture ou un devis"}
        onFiles={async ([file]) => {
          const r = await act.run(() => sendFile(`${base}/documents`, file));
          if (r) setDoc({ id: r.id, filename: r.filename });
        }}
      />
      <Checkbox label="Montant fondé sur un devis estimatif" checked={f.estimate} onChange={(v) => setF({ ...f, estimate: v })} />
      <div>
        <Button type="submit" variant="secondaire" busy={act.busy}>
          Ajouter la retenue
        </Button>
      </div>
    </form>
  );
}

function RefundForm({ base, expected, onDone }: { base: string; expected: number; onDone: () => void }) {
  const [f, setF] = useState({ amount_cents: expected as number | null, paid_on: todayIso(), method: "virement", reference: "" });
  const act = useAction();
  return (
    <form
      className="stack-sm"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await act.run(() => post(`${base}/refunds`, { ...f, amount_cents: f.amount_cents ?? 0 }), "Restitution enregistrée.")) onDone();
      }}
    >
      <h3>Enregistrer une restitution</h3>
      <p className="small muted">Bailivo n’émet aucun paiement : indiquez le virement ou le chèque réellement effectué.</p>
      <ErrorBox error={act.error} />
      <div className="form-row">
        <MoneyInput label="Montant versé" cents={f.amount_cents} error={fieldError(act.error, "amount_cents")} onChange={(c) => setF({ ...f, amount_cents: c })} />
        <Input label="Date" type="date" value={f.paid_on} error={fieldError(act.error, "paid_on")} onChange={(e) => setF({ ...f, paid_on: e.target.value })} />
        <Select label="Mode" value={f.method} options={options({ virement: "Virement", cheque: "Chèque", especes: "Espèces", autre: "Autre" })} onChange={(e) => setF({ ...f, method: e.target.value })} />
        <Input label="Référence" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} />
      </div>
      <div>
        <Button type="submit" busy={act.busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

/** Décompte vu par le locataire (après validation seulement). */
export function TenantDeposit({ deposit }: { deposit: any }) {
  if (!deposit) return null;
  const s = deposit.statement;
  return (
    <Card title="Décompte du dépôt de garantie" actions={<Status value={s.status} labels={DEPOSIT_STATUS} />}>
      <div className="stack">
        <Deadline d={deposit} />
        <Breakdown d={deposit} />
        {deposit.deductions.some((x: any) => x.document_id) ? (
          <ul className="list-plain small">
            {deposit.deductions
              .filter((x: any) => x.document_id)
              .map((x: any) => (
                <li key={x.id}>
                  <a href={docUrl(x.document_id)} target="_blank" rel="noreferrer">
                    Justificatif : {x.label}
                  </a>
                </li>
              ))}
          </ul>
        ) : null}
        {s.document_id ? (
          <a className="bv-btn bv-btn-secondaire bv-btn-petit" href={docUrl(s.document_id)} target="_blank" rel="noreferrer">
            Télécharger le décompte
          </a>
        ) : null}
        <RuleNote version={s.rule_version}>{deposit.rule_text}</RuleNote>
      </div>
    </Card>
  );
}
